import React, { useState } from 'react';
import { Modal } from '../../../components/Modal';
import { UserService } from '../../configuracoes/usuario/Service';
import AvatarUser from '../../../components/AvatarUser';
import ClientGallery from './gallery/ClientGallery';
import { AgendamentosClient } from '../Agendamentos';
import ClientesForm from '../Form';
import { DataRow, formatPhone, getSelfSchedulingLabel, onlyDigits } from '../helpers';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { useAppContext } from '../../../contexts/AppContext';
import { ChevronLeft, Mail, MessageCircle, Phone, Send, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const userService = new UserService();

type TabKey = 'details' | 'schedules' | 'gallery';

const tabs: { key: TabKey; label: string }[] = [
    { key: 'details', label: 'Dados' },
    { key: 'schedules', label: 'Agendamentos' },
    { key: 'gallery', label: 'Galeria' },
];

const ActionLink = ({ href, icon: Icon, label, external }: { href?: string; icon: LucideIcon; label: string; external?: boolean }) =>
    href ? (
        <a
            className="cli-action"
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
            <Icon aria-hidden="true" />
            {label}
        </a>
    ) : (
        <span className="cli-action" aria-disabled="true">
            <Icon aria-hidden="true" />
            {label}
        </span>
    );

export const ViewClient: React.FC<{
    data: DataRow;
    atendentes: any[];
    isSideBySide: boolean;
    onBack: () => void;
    onClientUpdated: () => void;
    onClientRemoved: () => void;
}> = ({ data, atendentes, isSideBySide, onBack, onClientUpdated, onClientRemoved }) => {
    const { setParams } = useParamsContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [openRemove, setOpenRemove] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [tab, setTab] = useState<TabKey>('details');

    const phoneDigits = onlyDigits(data.phoneNumber);
    const clientName = data.displayName || data.email || 'Sem nome';
    const atendentesNames = (data.atendentes || [])
        .map((uid) => {
            const atendente = atendentes.find((item) => item.uid === uid);
            return atendente?.displayName || atendente?.email;
        })
        .filter(Boolean);

    const removeClient = async () => {
        try {
            setLoading(true);
            await userService.removeClient(data.uid);
            successSnackbar('Cliente removido do estabelecimento.');
            onClientRemoved();
        } catch (e) {
            console.error('Erro ao remover cliente:', e);
            errorSnackbar('Não foi possível remover o cliente.');
            setLoading(false);
            setOpenRemove(false);
        }
    };

    return (
        <div className="cli-detail">
            <div className="cli-detail-bar">
                <button type="button" className="sf-back-button -ml-1" onClick={onBack}>
                    {isSideBySide ? <X aria-hidden /> : <ChevronLeft aria-hidden />}
                    {isSideBySide ? 'Fechar' : 'Clientes'}
                </button>
                <button type="button" className="sf-link" onClick={() => setOpenEdit(true)}>
                    Editar
                </button>
            </div>

            <div className="cli-hero">
                <AvatarUser photoURL={data.photoURL} name={data.displayName || 'Cliente'} size="h-20 w-20 !text-2xl" />
                <h2 className="cli-hero-name">{clientName}</h2>
                {(data.disabled || data.selfSchedulingBlocked) && (
                    <div className="sf-tags justify-center !mt-0">
                        {data.disabled && (
                            <span className="sf-tag" data-tone="gray">
                                Conta desativada
                            </span>
                        )}
                        {data.selfSchedulingBlocked && (
                            <span className="sf-tag" data-tone="orange">
                                Autoagendamento bloqueado
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="cli-actions">
                <button
                    type="button"
                    className="cli-action"
                    onClick={() => setParams({ action: 'mensagem', id: data.uid })}
                >
                    <Send aria-hidden />
                    Mensagem
                </button>
                <ActionLink
                    href={phoneDigits ? `https://wa.me/${phoneDigits}` : undefined}
                    icon={MessageCircle}
                    label="WhatsApp"
                    external
                />
                <ActionLink
                    href={phoneDigits ? `tel:+${phoneDigits}` : undefined}
                    icon={Phone}
                    label="Ligar"
                />
                <ActionLink
                    href={data.email ? `mailto:${data.email}` : undefined}
                    icon={Mail}
                    label="E-mail"
                />
            </div>

            <div className="sf-segmented cli-section" role="group" aria-label="Informações do cliente">
                {tabs.map(({ key, label }) => (
                    <button key={key} type="button" aria-pressed={tab === key} onClick={() => setTab(key)}>
                        {label}
                    </button>
                ))}
            </div>

            <div className="cli-section">
                {tab === 'details' && (
                    <div className="space-y-5">
                        <div className="sf-group">
                            <div className="sf-info-row">
                                <span>Telefone</span>
                                <span>{formatPhone(data.phoneNumber) || 'Não informado'}</span>
                            </div>
                            <div className="sf-info-row">
                                <span>E-mail</span>
                                <span>
                                    {data.email || 'Não informado'}
                                    {!!data.email && !data.emailVerified && (
                                        <span className="block text-xs text-warning">Não verificado</span>
                                    )}
                                </span>
                            </div>
                            <div className="sf-info-row">
                                <span>Atendentes</span>
                                <span>{atendentesNames.length ? atendentesNames.join(', ') : 'Não definidos'}</span>
                            </div>
                            <div className="sf-info-row">
                                <span>Autoagendamento</span>
                                <span>{getSelfSchedulingLabel(data)}</span>
                            </div>
                        </div>

                        <div className="sf-group">
                            <button type="button" className="sf-destructive-row" onClick={() => setOpenRemove(true)}>
                                Remover cliente
                            </button>
                        </div>
                    </div>
                )}

                {tab === 'schedules' && <AgendamentosClient clientId={data.uid} />}

                {tab === 'gallery' && <ClientGallery clientId={data.uid} />}
            </div>

            {openEdit && (
                <Modal onClose={() => setOpenEdit(false)} title="Editar cliente" desktopClassName="sm:!max-w-xl">
                    <ClientesForm
                        doc={data}
                        cancel={() => setOpenEdit(false)}
                        callback={() => {
                            setOpenEdit(false);
                            onClientUpdated();
                        }}
                    />
                </Modal>
            )}

            {openRemove && (
                <Modal
                    onClose={() => setOpenRemove(false)}
                    title="Remover cliente?"
                    desktopClassName="sm:!max-w-md"
                >
                    <p className="text-sm text-base-content/70">
                        {clientName} deixa de aparecer na lista deste estabelecimento. A conta da pessoa e os
                        agendamentos existentes não são alterados, e ela pode ser adicionada novamente.
                    </p>
                    <div className="mt-5 flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setOpenRemove(false)}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-error" onClick={removeClient} disabled={loading}>
                            {loading && <span className="loading loading-spinner loading-sm"></span>}
                            Remover
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};
